export type PermalinkOptionType = "CALLS" | "PUTS";

export interface PermalinkOptionSelection {
  optionType: PermalinkOptionType;
  strikePriceCents: number;
}

export interface DashboardPermalink {
  symbol: string | null;
  expiration: string | null;
  optionType: PermalinkOptionType | null;
  strikePriceCents: number | null;
}

function validExpiration(value: string | null): string | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const parsed = new Date(`${value}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) ||
    parsed.toISOString().slice(0, 10) !== value
    ? null
    : value;
}

export function readDashboardPermalink(
  search: string,
  allowedSymbols: readonly string[],
): DashboardPermalink {
  const query = new URLSearchParams(search);
  const requestedSymbol = query.get("symbol")?.trim().toUpperCase() ?? null;
  const requestedOptionType =
    query.get("type")?.trim().toUpperCase() ?? null;
  const strikePriceText = query.get("striking_price");
  const strikePriceCents =
    strikePriceText &&
    /^\d+$/.test(strikePriceText) &&
    Number.isSafeInteger(Number(strikePriceText)) &&
    Number(strikePriceText) > 0
      ? Number(strikePriceText)
      : null;
  const optionType =
    requestedOptionType === "CALLS" || requestedOptionType === "PUTS"
      ? requestedOptionType
      : null;
  const hasOptionSelection = optionType !== null && strikePriceCents !== null;

  return {
    symbol:
      requestedSymbol && allowedSymbols.includes(requestedSymbol)
        ? requestedSymbol
        : null,
    expiration: validExpiration(query.get("expiration")),
    optionType: hasOptionSelection ? optionType : null,
    strikePriceCents: hasOptionSelection ? strikePriceCents : null,
  };
}

export function dashboardPermalinkHref(
  currentHref: string,
  selection: {
    symbol: string;
    expiration?: string | null;
    optionType?: PermalinkOptionType | null;
    strikePriceCents?: number | null;
  },
): string {
  const url = new URL(currentHref);
  url.searchParams.set("symbol", selection.symbol.toUpperCase());
  if (selection.expiration) {
    url.searchParams.set("expiration", selection.expiration);
  } else {
    url.searchParams.delete("expiration");
  }
  if (
    selection.optionType &&
    selection.strikePriceCents &&
    Number.isSafeInteger(selection.strikePriceCents) &&
    selection.strikePriceCents > 0
  ) {
    url.searchParams.set("type", selection.optionType);
    url.searchParams.set(
      "striking_price",
      String(selection.strikePriceCents),
    );
  } else {
    url.searchParams.delete("type");
    url.searchParams.delete("striking_price");
  }
  return `${url.pathname}${url.search}${url.hash}`;
}
